<template>
  <div>
    <section class="page-title text-center mb-5">
      <div class="container">
        <div class="content-box">
          <h1>
            {{ $t("Reservation Details") }}
          </h1>
        </div>
      </div>
    </section>
    <div style="background-color: white; padding-top: 36px; padding-bottom: 150px">
      <div class="container">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-3 text-muted">{{ $t('Loading reservation details...') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-5">
          <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5 class="text-warning">{{ $t('Error loading reservation') }}</h5>
          <p class="text-muted">{{ error }}</p>
          <button @click="loadReservation" class="btn btn-primary">
            <i class="fa fa-refresh"></i> {{ $t('Try Again') }}
          </button>
        </div>

        <!-- Reservation Details -->
        <div v-else-if="item.id">
          <!-- Desktop Table View -->
          <div class="d-none d-lg-block">
            <!-- Personal Information Table -->
            <div class="mb-4">
              <h4 class="mb-3">{{ $t('Personal Information') }}</h4>
              <table class="table table-bordered table-responsive">
                <thead>
                  <tr>
                    <th scope="col" style="width:100px">#</th>
                    <th scope="col">{{ $t('First Name') }}</th>
                    <th scope="col">{{ $t('Last Name') }}</th>
                    <th scope="col">{{ $t('Address') }}</th>
                    <th scope="col">{{ $t('Zip') }}</th>
                    <th scope="col">{{ $t('City') }}</th>
                    <th scope="col">{{ $t('Country') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">{{ $codePadding(item.id) }}</th>
                    <td>{{ item.fname }}</td>
                    <td>{{ item.lname }}</td>
                    <td>{{ item.address }}</td>
                    <td>{{ item.zip }}</td>
                    <td>{{ item.city }}</td>
                    <td>{{ item.country }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Service Information Table -->
            <div class="mb-4">
              <h4 class="mb-3">{{ $t('Service Information') }}</h4>
              <table class="table table-bordered table-responsive">
                <thead>
                  <tr>
                    <th scope="col">{{ $t('Payment Method') }}</th>
                    <th scope="col">{{ $t('Massage') }}</th>
                    <th scope="col">{{ $t('Plan') }}</th>
                    <th scope="col">{{ $t('Day') }}</th>
                    <th scope="col">{{ $t('Time') }}</th>
                    <th scope="col">{{ $t('Masser') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td v-if="item.usersId">
                      {{ item.paymentMethod }}
                      -
                      {{ item.usersId.paymentDone ? $t("And Done") : $t("But Not Complete") }}
                    </td>
                    <td v-if="item.massageId">{{ $getByLang(item.massageId.name) }}</td>
                    <td v-if="item.pricesId">
                      {{ $getByLang(item.pricesId.name) }} ({{ item.pricesId.price }} €)
                    </td>
                    <td>{{ $durationFormat(item.start) }}</td>
                    <td>{{ $durationFormatStartToEnd(item.start, item.end) }}</td>
                    <td>{{ item.adminsId.fullName }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Mobile Card View -->
          <div class="d-lg-none">
            <!-- Personal Information Card -->
            <div class="reservation-detail-card mb-4">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">
                    <i class="fa fa-user me-2"></i>
                    {{ $t('Personal Information') }}
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-6 mb-3">
                      <strong>{{ $t('Reservation ID') }}:</strong>
                      <div>#{{ $codePadding(item.id) }}</div>
                    </div>
                    <div class="col-6 mb-3">
                      <strong>{{ $t('First Name') }}:</strong>
                      <div>{{ item.fname }}</div>
                    </div>
                    <div class="col-6 mb-3">
                      <strong>{{ $t('Last Name') }}:</strong>
                      <div>{{ item.lname }}</div>
                    </div>
                    <div class="col-12 mb-3">
                      <strong>{{ $t('Address') }}:</strong>
                      <div>{{ item.address }}</div>
                    </div>
                    <div class="col-4 mb-3">
                      <strong>{{ $t('Zip') }}:</strong>
                      <div>{{ item.zip }}</div>
                    </div>
                    <div class="col-4 mb-3">
                      <strong>{{ $t('City') }}:</strong>
                      <div>{{ item.city }}</div>
                    </div>
                    <div class="col-4 mb-3">
                      <strong>{{ $t('Country') }}:</strong>
                      <div>{{ item.country }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Service Information Card -->
            <div class="reservation-detail-card mb-4">
              <div class="card">
                <div class="card-header">
                  <h5 class="mb-0">
                    <i class="fa fa-spa me-2"></i>
                    {{ $t('Service Information') }}
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 mb-3" v-if="item.usersId">
                      <strong>{{ $t('Payment Method') }}:</strong>
                      <div>
                        {{ item.paymentMethod }}
                        -
                        <span :class="item.usersId.paymentDone ? 'text-success' : 'text-warning'">
                          {{ item.usersId.paymentDone ? $t("And Done") : $t("But Not Complete") }}
                        </span>
                      </div>
                    </div>
                    <div class="col-12 mb-3" v-if="item.massageId">
                      <strong>{{ $t('Massage') }}:</strong>
                      <div>{{ $getByLang(item.massageId.name) }}</div>
                    </div>
                    <div class="col-12 mb-3" v-if="item.pricesId">
                      <strong>{{ $t('Plan') }}:</strong>
                      <div>{{ $getByLang(item.pricesId.name) }} ({{ item.pricesId.price }} €)</div>
                    </div>
                    <div class="col-6 mb-3">
                      <strong>{{ $t('Day') }}:</strong>
                      <div>{{ $durationFormat(item.start) }}</div>
                    </div>
                    <div class="col-6 mb-3">
                      <strong>{{ $t('Time') }}:</strong>
                      <div>{{ $durationFormatStartToEnd(item.start, item.end) }}</div>
                    </div>
                    <div class="col-12 mb-3">
                      <strong>{{ $t('Masser') }}:</strong>
                      <div>{{ item.adminsId.fullName }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Back Button -->
          <div class="text-center mt-4">
            <router-link to="/myreservations" class="btn btn-secondary">
              <i class="fa fa-arrow-left me-2"></i>
              {{ $t('Back to Reservations') }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  data() {
    return {
      item: {
        id: null,
        sexType: null,
        fname: null,
        lname: null,
        paymentMethod: null,
        paymentDone: false,
        address: null,
        zip: null,
        city: null,
        country: null,
        start: null,
        end: null,
        adminsId: {
          fullName: "test",
        },
      },
      loading: true,
      error: null,
    };
  },

  methods: {
    async loadReservation() {
      try {
        this.loading = true;
        this.error = null;
        
        const id = this.$route.params.id;
        const response = await this.$http.get("appointments/" + id);
        
        this.item = response.data.data;

        // Check if user has permission to view this reservation
        const user = JSON.parse(localStorage.userKB);
        if (user.id != this.item.usersId.id) {
          this.error = this.$t('You do not have permission to view this reservation');
          return;
        }
      } catch (error) {
        console.error('Error loading reservation:', error);
        this.error = this.$t('Failed to load reservation details. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  },
  
  async created() {
    await this.loadReservation();
  },
};
</script>

<style scoped>
/* Mobile Card Styles */
.reservation-detail-card .card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: box-shadow 0.3s ease;
}

.reservation-detail-card .card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.reservation-detail-card .card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 16px 20px;
}

.reservation-detail-card .card-header h5 {
  color: #495057;
  font-weight: 600;
  margin: 0;
}

.reservation-detail-card .card-header i {
  color: #007bff;
}

.reservation-detail-card .card-body {
  padding: 20px;
}

.reservation-detail-card .card-body .row > div {
  padding: 4px 8px;
}

.reservation-detail-card .card-body strong {
  color: #495057;
  font-size: 0.9rem;
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
}

.reservation-detail-card .card-body div {
  color: #6c757d;
  font-size: 0.85rem;
  word-break: break-word;
  line-height: 1.4;
}

/* Desktop Table Improvements */
.table-responsive {
  border-radius: 8px;
  overflow: hidden;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
  padding: 12px;
}

.table tbody td {
  padding: 12px;
  vertical-align: middle;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Section Headers */
h4 {
  color: #495057;
  font-weight: 600;
  border-bottom: 2px solid #007bff;
  padding-bottom: 8px;
  display: inline-block;
}

/* Button Improvements */
.btn {
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

/* Loading and Error States */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

.text-warning {
  color: #ffc107 !important;
}

.text-success {
  color: #28a745 !important;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .reservation-detail-card .card-body .row > div {
    padding: 2px 4px;
  }
  
  .reservation-detail-card .card-body strong {
    font-size: 0.8rem;
  }
  
  .reservation-detail-card .card-body div {
    font-size: 0.8rem;
  }
  
  .reservation-detail-card .card-header {
    padding: 12px 16px;
  }
  
  .reservation-detail-card .card-body {
    padding: 16px;
  }

  .reservation-detail-card .card-header h5 {
    font-size: 1rem;
  }

  .btn {
    padding: 6px 12px;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .reservation-detail-card .card-body .row > div {
    padding: 1px 2px;
  }
  
  .reservation-detail-card .card-body strong {
    font-size: 0.75rem;
  }
  
  .reservation-detail-card .card-body div {
    font-size: 0.75rem;
  }
  
  .reservation-detail-card .card-header {
    padding: 10px 12px;
  }
  
  .reservation-detail-card .card-body {
    padding: 12px;
  }
}

/* Payment Status Styling */
.text-success {
  font-weight: 600;
}

.text-warning {
  font-weight: 600;
}

/* Icon Styling */
.fa-user, .fa-spa {
  font-size: 1.1em;
}

/* Card Spacing */
.reservation-detail-card {
  margin-bottom: 1.5rem;
}

/* Back Button Styling */
.btn-secondary i {
  margin-right: 6px;
}

/* Loading State */
.loading {
  opacity: 0.6;
  pointer-events: none;
}
</style>
